import { Check, Loader2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { Button, Badge, Input } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import {
  fetchShotLatestTranscription,
  fetchProjectCast,
  fetchProjectLocations,
  applyShotTranscription,
  type AnimaticSegmentTranscriptionRow,
  type MatchedCharacter,
  type MatchedLocation,
} from "@/lib/workspace-api"
import { cn } from "@/lib/utils"

type CastRow = { id: number; name: string; role: string | null; reference_sheet_url: string | null }
type LocationRow = { id: number; name: string; variants: { id: number; name: string; is_default?: boolean }[] }

type Props = {
  projectId: number
  shotId: number
  onDataChanged: () => void
}

export function ShotTranscriptionView({ projectId, shotId, onDataChanged }: Props) {
  const { t } = useTranslation(["shot", "common"])
  const [loading, setLoading] = useState(true)
  const [transcription, setTranscription] = useState<AnimaticSegmentTranscriptionRow | null>(null)
  const [matchedChars, setMatchedChars] = useState<MatchedCharacter[]>([])
  const [matchedLocation, setMatchedLocation] = useState<MatchedLocation | null>(null)

  const [projectCast, setProjectCast] = useState<CastRow[]>([])
  const [projectLocations, setProjectLocations] = useState<LocationRow[]>([])

  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)
  const [charFilter, setCharFilter] = useState("")
  const [locFilter, setLocFilter] = useState("")
  const [storyboardPrompt, setStoryboardPrompt] = useState("")
  const [videoPrompt, setVideoPrompt] = useState("")

  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([
      fetchShotLatestTranscription(projectId, shotId),
      fetchProjectCast(projectId),
      fetchProjectLocations(projectId),
    ])
      .then(([data, cast, locations]) => {
        if (cancelled) return

        setProjectCast(cast)
        setProjectLocations(locations.map((l) => ({ id: l.id, name: l.name, variants: l.variants })))

        if (data.ok && data.transcription) {
          setTranscription(data.transcription)
          const mc = data.transcription.matched_characters ?? []
          const ml = data.transcription.matched_location ?? null
          setMatchedChars(mc)
          setMatchedLocation(ml)
          setCheckedIds(new Set(mc.map((c) => c.id)))
          setSelectedLocationId(ml?.id ?? null)
          setStoryboardPrompt(data.transcription.result?.notes_for_first_frame_prompt ?? "")
          setVideoPrompt(data.transcription.result?.notes_for_video_prompt ?? "")
        } else {
          setTranscription(null)
          setError(data.error === "no_transcription" ? null : (data.error ?? null))
        }
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("transcription.loadFailed"))
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [projectId, shotId, t])

  const toggleChar = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setSaved(false)
  }

  const handleApply = async () => {
    setSaving(true)
    setError(null)
    try {
      const result = await applyShotTranscription(
        projectId,
        shotId,
        [...checkedIds],
        selectedLocationId,
        storyboardPrompt,
        videoPrompt,
      )
      if (result.ok) {
        setSaved(true)
        onDataChanged()
      } else {
        setError(result.error ?? t("transcription.approveFailed"))
      }
    } catch {
      setError(t("transcription.networkError"))
    } finally {
      setSaving(false)
    }
  }

  const filteredCast = useMemo(() => {
    const q = charFilter.trim().toLowerCase()
    return q
      ? projectCast.filter((c) => c.name.toLowerCase().includes(q) || (c.role ?? "").toLowerCase().includes(q))
      : projectCast
  }, [charFilter, projectCast])

  const filteredLocations = useMemo(() => {
    const q = locFilter.trim().toLowerCase()
    return q ? projectLocations.filter((l) => l.name.toLowerCase().includes(q)) : projectLocations
  }, [locFilter, projectLocations])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
      </div>
    )
  }

  if (!transcription) {
    return <div className="py-10 text-center text-sm text-ui-text-muted">{error ?? t("transcription.notDone")}</div>
  }

  const r = transcription.result

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-ui-text-primary">{t("transcription.title")}</h3>

      {r ? (
        <div className="space-y-3 text-xs text-ui-text-secondary">
          {r.summary ? <p className="text-[11px] leading-relaxed text-ui-text-primary">{r.summary}</p> : null}

          <div className="flex flex-wrap gap-1.5">
            {r.shot_type ? <Badge tone="violet">{r.shot_type}</Badge> : null}
            {r.camera?.movement ? <Badge tone="neutral">cam: {r.camera.movement}</Badge> : null}
            {r.camera?.angle ? <Badge tone="neutral">angle: {r.camera.angle}</Badge> : null}
          </div>

          {r.environment || r.lighting ? (
            <div className="space-y-0.5">
              <span className="text-[9px] font-bold text-ui-text-muted uppercase">
                {t("transcription.environment")}
              </span>
              {r.environment ? <p>{r.environment}</p> : null}
              {r.lighting ? (
                <p className="text-ui-text-muted">{t("transcription.lighting", { value: r.lighting })}</p>
              ) : null}
            </div>
          ) : null}

          {r.characters && r.characters.length > 0 ? (
            <div className="space-y-1">
              <span className="text-[9px] font-bold text-ui-text-muted uppercase">
                {t("transcription.charactersFromVideo")}
              </span>
              {r.characters.map((c, i) => (
                <div key={i} className="rounded-md border border-white/5 bg-ui-surface-sunken px-2 py-1.5">
                  <span className="font-medium text-ui-text-primary">{c.name}</span>
                  {c.character_id ? (
                    <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[9px] text-ui-accent-text dark:bg-violet-900/30 light:bg-clay-3">
                      id:{c.character_id}
                    </span>
                  ) : null}
                  {c.action ? <span className="ml-1.5 text-ui-text-secondary">— {c.action}</span> : null}
                </div>
              ))}
            </div>
          ) : null}

          {r.dialogue && r.dialogue.length > 0 ? (
            <div className="space-y-1">
              <span className="text-[9px] font-bold text-ui-text-muted uppercase">{t("transcription.dialogues")}</span>
              {r.dialogue.map((d, i) => (
                <div key={i} className="rounded-md border border-white/5 bg-ui-surface-sunken px-2 py-1.5">
                  <span className="font-medium text-ui-text-primary">{d.speaker}:</span>{" "}
                  <span className="text-ui-text-secondary italic">&ldquo;{d.text}&rdquo;</span>
                  {d.start_sec != null || d.end_sec != null ? (
                    <span className="ml-2 text-[10px] text-ui-text-muted">
                      [{d.start_sec ?? "?"}s – {d.end_sec ?? "?"}s]
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {r.notes_for_first_frame_prompt || r.notes_for_video_prompt ? (
            <div className="space-y-2">
              <span className="text-[9px] font-bold text-ui-text-muted uppercase">
                {t("transcription.editablePrompts")}
              </span>
              {r.notes_for_first_frame_prompt ? (
                <div className="space-y-1">
                  <label className="text-[9px] text-ui-text-secondary">{t("transcription.firstFramePrompt")}</label>
                  <textarea
                    className="w-full rounded-lg border border-ui-border bg-ui-surface-sunken px-2.5 py-1.5 text-[11px] leading-relaxed text-ui-text-primary placeholder:text-ui-text-secondary focus:border-ui-accent focus:ring-1 focus:ring-[var(--ui-focus-ring)]/30 focus:outline-none"
                    rows={3}
                    value={storyboardPrompt}
                    onChange={(e) => {
                      setStoryboardPrompt(e.target.value)
                      setSaved(false)
                    }}
                  />
                </div>
              ) : null}
              {r.notes_for_video_prompt ? (
                <div className="space-y-1">
                  <label className="text-[9px] text-ui-text-secondary">{t("transcription.videoPrompt")}</label>
                  <textarea
                    className="w-full rounded-lg border border-ui-border bg-ui-surface-sunken px-2.5 py-1.5 text-[11px] leading-relaxed text-ui-text-primary placeholder:text-ui-text-secondary focus:border-ui-accent focus:ring-1 focus:ring-[var(--ui-focus-ring)]/30 focus:outline-none"
                    rows={3}
                    value={videoPrompt}
                    onChange={(e) => {
                      setVideoPrompt(e.target.value)
                      setSaved(false)
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <section className="space-y-2 border-t border-white/[0.06] pt-3">
        <h4 className="text-[9px] font-bold tracking-wider text-ui-text-muted uppercase">
          {t("characters.charactersSection")}
          {matchedChars.length > 0 ? (
            <span className="ml-1 font-normal text-ui-link normal-case">
              {t("characters.recognized", { count: matchedChars.length })}
            </span>
          ) : null}
        </h4>
        <Input
          placeholder={t("placeholders.searchNameRole")}
          value={charFilter}
          onChange={(e) => setCharFilter(e.target.value)}
          className="text-xs"
        />
        <div className="max-h-[200px] space-y-1 overflow-y-auto pr-1">
          {filteredCast.length === 0 ? (
            <p className="text-xs text-ui-text-secondary">{t("characters.noCharacters")}</p>
          ) : (
            filteredCast.map((c) => {
              const isMatched = matchedChars.some((m) => m.id === c.id)
              return (
                <label
                  key={c.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors",
                    checkedIds.has(c.id)
                      ? "border-ui-accent/40 text-ui-text-primary dark:bg-violet-950/20 light:border-ui-border-strong light:bg-clay-2 light:shadow-[var(--shadow-clay-inset)]"
                      : "border-ui-border bg-ui-surface-sunken text-ui-text-secondary",
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-zinc-600 text-violet-500 focus:ring-[var(--ui-focus-ring)] dark:bg-zinc-800 light:bg-clay-2"
                    checked={checkedIds.has(c.id)}
                    onChange={() => toggleChar(c.id)}
                  />
                  {c.reference_sheet_url ? (
                    <img
                      src={c.reference_sheet_url}
                      alt=""
                      className="h-7 w-7 shrink-0 rounded border border-ui-border object-cover"
                    />
                  ) : null}
                  <span className="font-medium">{c.name}</span>
                  {c.role ? <span className="text-ui-text-muted">({c.role})</span> : null}
                  {isMatched ? (
                    <Badge tone="violet" className="ml-auto text-[8px]">
                      AI
                    </Badge>
                  ) : null}
                </label>
              )
            })
          )}
        </div>
      </section>

      <section className="space-y-2 border-t border-white/[0.06] pt-3">
        <h4 className="text-[9px] font-bold tracking-wider text-ui-text-muted uppercase">
          {t("location.shotLocation")}
          {matchedLocation ? (
            <span className="ml-1 font-normal text-green-400 normal-case">
              {t("location.recognized", { name: matchedLocation.name })}
            </span>
          ) : null}
        </h4>
        {projectLocations.length > 5 ? (
          <Input
            placeholder={t("placeholders.searchLocation")}
            value={locFilter}
            onChange={(e) => setLocFilter(e.target.value)}
            className="text-xs"
          />
        ) : null}
        <div className="max-h-[160px] space-y-1 overflow-y-auto pr-1">
          {filteredLocations.length === 0 ? (
            <p className="text-xs text-ui-text-secondary">{t("transcription.noLocations")}</p>
          ) : (
            <>
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors",
                  selectedLocationId === null
                    ? "border-amber-500/40 bg-amber-950/20 text-ui-text-primary"
                    : "border-ui-border bg-ui-surface-sunken text-ui-text-secondary",
                )}
              >
                <input
                  type="radio"
                  name="transcription-location"
                  className="h-3.5 w-3.5 border-zinc-600 text-amber-500 focus:ring-amber-500/50 dark:bg-zinc-800 light:bg-clay-2"
                  checked={selectedLocationId === null}
                  onChange={() => {
                    setSelectedLocationId(null)
                    setSaved(false)
                  }}
                />
                <span className="italic">{t("location.noLocation")}</span>
              </label>
              {filteredLocations.map((loc) => {
                const isMatched = matchedLocation?.id === loc.id
                return (
                  <label
                    key={loc.id}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors",
                      selectedLocationId === loc.id
                        ? "border-green-500/40 bg-green-950/20 text-ui-text-primary"
                        : "border-ui-border bg-ui-surface-sunken text-ui-text-secondary",
                    )}
                  >
                    <input
                      type="radio"
                      name="transcription-location"
                      className="h-3.5 w-3.5 border-zinc-600 text-green-500 focus:ring-green-500/50 dark:bg-zinc-800 light:bg-clay-2"
                      checked={selectedLocationId === loc.id}
                      onChange={() => {
                        setSelectedLocationId(loc.id)
                        setSaved(false)
                      }}
                    />
                    <span className="font-medium">{loc.name}</span>
                    {isMatched ? (
                      <Badge tone="emerald" className="ml-auto text-[8px]">
                        AI
                      </Badge>
                    ) : null}
                  </label>
                )
              })}
            </>
          )}
        </div>
      </section>

      {error ? <p className="text-xs text-ui-status-failed">{error}</p> : null}
      {saved ? <p className="text-xs text-green-400">{t("transcription.approved")}</p> : null}

      <div className="flex gap-2 border-t border-white/[0.06] pt-3">
        <Button
          variant="default"
          size="sm"
          type="button"
          disabled={saving}
          onClick={() => void handleApply()}
          className="flex-1 justify-center"
        >
          {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}
          {t("transcription.apply")}
        </Button>
      </div>
    </div>
  )
}
